import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner } from "components/ui/BootstrapCompat";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  FaFacebook,
  FaInstagram,
  FaTiktok,
  FaTwitch,
  FaYoutube,
} from "react-icons/fa";
import {
  FiDollarSign,
  FiGlobe,
  FiImage,
  FiLink,
  FiSave,
  FiUploadCloud,
  FiUser,
  FiWifi,
  FiX,
} from "react-icons/fi";
import useLocalStorage from "helpers/useLocalStorage";
import { errorAdd, successAdd } from "helpers/sweetalert";
import { URL_PHOTO_AW3 } from "constants/index";
import {
  donateStreamerUpdateProfile,
  getUserProfileData,
  uploadDonateProfileImage,
  userGetMe,
} from "services/user.service";
import {
  DONATE_BUSINESS_ID,
  donateErrorMessage,
} from "services/main/donate.service";

type SocialKey =
  | "youtube"
  | "twitch"
  | "tiktok"
  | "facebook"
  | "instagram"
  | "website";

type DonationSettingsForm = {
  currency: string;
  minAmount: string;
  maxAmount: string;
  suggestedAmounts: string;
  allowedPaymentMethods: string[];
  allowOfflineDonations: boolean;
};

type StreamerProfileForm = {
  name: string;
  username: string;
  image: string;
  coverImage: string;
  bio: string;
  isOnline: boolean;
  socialLinks: Record<SocialKey, string>;
  donationSettings: DonationSettingsForm;
};

type UploadField = "image" | "coverImage";

const STORAGE_KEY = "streamerProfileCustomizeDraft";
const LEGACY_STORAGE_KEY = "streamChannelCustomizeDraft";
const DEFAULT_PAYMENT_METHOD = "qr_payment";

const acceptedImageTypes = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/heic",
  "image/heif",
].join(",");

const socialFields: Array<{
  key: SocialKey;
  label: string;
  placeholder: string;
  icon: React.ComponentType<{ className?: string }>;
  iconClass: string;
}> = [
  {
    key: "youtube",
    label: "YOUTUBE",
    placeholder: "https://youtube.com/@yourname",
    icon: FaYoutube,
    iconClass: "text-red-600",
  },
  {
    key: "twitch",
    label: "TWITCH",
    placeholder: "https://twitch.tv/yourname",
    icon: FaTwitch,
    iconClass: "text-violet-600",
  },
  {
    key: "tiktok",
    label: "TIKTOK",
    placeholder: "https://tiktok.com/@yourname",
    icon: FaTiktok,
    iconClass: "text-slate-900",
  },
  {
    key: "facebook",
    label: "FACEBOOK",
    placeholder: "https://facebook.com/yourname",
    icon: FaFacebook,
    iconClass: "text-blue-600",
  },
  {
    key: "instagram",
    label: "INSTAGRAM",
    placeholder: "https://instagram.com/yourname",
    icon: FaInstagram,
    iconClass: "text-pink-600",
  },
  {
    key: "website",
    label: "ອື່ນໆ",
    placeholder: "https://...",
    icon: FiGlobe,
    iconClass: "text-slate-700",
  },
];

const paymentMethodOptions = [
  { value: DEFAULT_PAYMENT_METHOD, label: "QR Payment" },
];

const emptyLinks = socialFields.reduce(
  (links, field) => ({ ...links, [field.key]: "" }),
  {} as Record<SocialKey, string>,
);

const toStringValue = (value: unknown) => {
  if (value === undefined || value === null) return "";
  return String(value);
};

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }

  return typeof value === "string" && value ? [value] : [];
};

const stringifySuggestedAmounts = (value: unknown) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item) && item > 0)
      .join(", ");
  }

  return toStringValue(value);
};

const getStreamerProfileData = (response: any) => {
  const root = response?.data?.data || response?.data || response || {};
  const baseUser =
    root?.user ||
    response?.data?.user ||
    response?.user ||
    getUserProfileData(response);
  const profile =
    root?.streamerProfile ||
    root?.streamer ||
    root?.profile ||
    root?.donateProfile ||
    root?.donationProfile ||
    baseUser?.streamerProfile ||
    baseUser?.streamer ||
    baseUser?.profile ||
    baseUser?.donateProfile ||
    baseUser?.donationProfile ||
    {};
  const merged = {
    ...baseUser,
    ...profile,
  };

  return {
    ...merged,
    username: profile?.username || baseUser?.username || merged?.username,
    socialLinks:
      profile?.socialLinks ||
      profile?.links ||
      baseUser?.socialLinks ||
      baseUser?.links ||
      merged?.socialLinks,
    donationSettings:
      profile?.donationSettings ||
      profile?.donateSettings ||
      baseUser?.donationSettings ||
      baseUser?.donateSettings ||
      merged?.donationSettings,
  };
};

const normalizeForm = (user: any): StreamerProfileForm => {
  const socialLinks = user?.socialLinks || user?.links || user?.socials || {};
  const donationSettings = user?.donationSettings || user?.donateSettings || {};
  const paymentMethods = toStringArray(donationSettings?.allowedPaymentMethods);

  return {
    name: toStringValue(user?.name ?? user?.displayName),
    username: toStringValue(user?.username),
    image: toStringValue(
      user?.image ??
        user?.imageUrl ??
        user?.avatarUrl ??
        user?.profileImage ??
        user?.profileImageUrl ??
        user?.avatar,
    ),
    coverImage: toStringValue(
      user?.coverImage ??
        user?.coverImageUrl ??
        user?.coverUrl ??
        user?.bannerImage ??
        user?.cover,
    ),
    bio: toStringValue(user?.bio ?? user?.description),
    isOnline: Boolean(user?.isOnline ?? true),
    socialLinks: {
      ...emptyLinks,
      ...socialLinks,
    },
    donationSettings: {
      currency: toStringValue(donationSettings?.currency ?? "LAK"),
      minAmount: toStringValue(donationSettings?.minAmount ?? 10000),
      maxAmount: toStringValue(donationSettings?.maxAmount ?? 2000000),
      suggestedAmounts:
        stringifySuggestedAmounts(donationSettings?.suggestedAmounts) ||
        "10000, 20000, 50000",
      allowedPaymentMethods: paymentMethods.length
        ? paymentMethods
        : [DEFAULT_PAYMENT_METHOD],
      allowOfflineDonations:
        typeof donationSettings?.allowOfflineDonations === "boolean"
          ? donationSettings.allowOfflineDonations
          : Boolean(donationSettings?.allowOfflineDonations ?? false),
    },
  };
};

const getImageSrc = (value: string) => {
  if (!value) return "";
  if (
    value.startsWith("http") ||
    value.startsWith("/") ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) {
    return value;
  }

  return `${URL_PHOTO_AW3}${value}`;
};

const getFileType = (file: File) => {
  if (file.type) return file.type;
  const extension = file.name.split(".").pop()?.toLowerCase();
  const fallbackTypes: Record<string, string> = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    webp: "image/webp",
    heic: "image/heic",
    heif: "image/heif",
  };

  return fallbackTypes[extension || ""] || "";
};

const isAllowedImage = (file: File) => {
  const fileType = getFileType(file);
  return (
    fileType.startsWith("image/") ||
    /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)
  );
};

const parseAmount = (value: string, message: string) => {
  const normalizedValue = value.replace(/,/g, "").trim();
  const amount = Number(normalizedValue);

  if (!normalizedValue || !Number.isFinite(amount) || amount < 0) {
    throw new Error(message);
  }

  return amount;
};

const parseSuggestedAmounts = (value: string) => {
  if (!value.trim()) return [];

  return value.split(",").map((item) => {
    const amount = Number(item.trim());
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error("ຈຳນວນແນະນຳບໍ່ຖືກຕ້ອງ");
    }
    return amount;
  });
};

const buildPayload = (form: StreamerProfileForm) => {
  const name = form.name.trim();
  const minAmount = parseAmount(
    form.donationSettings.minAmount,
    "ຈຳນວນຂັ້ນຕ່ຳບໍ່ຖືກຕ້ອງ",
  );
  const maxAmount = parseAmount(
    form.donationSettings.maxAmount,
    "ຈຳນວນສູງສຸດບໍ່ຖືກຕ້ອງ",
  );
  const allowedPaymentMethods =
    form.donationSettings.allowedPaymentMethods.filter(Boolean);

  if (!name) throw new Error("ກະລຸນາປ້ອນຊື່ສະຕຣີມເມີ");
  if (maxAmount < minAmount) {
    throw new Error("ຈຳນວນສູງສຸດຕ້ອງຫຼາຍກວ່າຫຼືເທົ່າກັບຂັ້ນຕ່ຳ");
  }
  if (!allowedPaymentMethods.length) {
    throw new Error("ກະລຸນາເລືອກຊ່ອງທາງຮັບເງິນ");
  }

  return {
    name,
    image: form.image.trim(),
    coverImage: form.coverImage.trim(),
    bio: form.bio.trim(),
    isOnline: form.isOnline,
    socialLinks: socialFields.reduce(
      (links, field) => ({
        ...links,
        [field.key]: form.socialLinks[field.key].trim(),
      }),
      {} as Record<SocialKey, string>,
    ),
    donationSettings: {
      currency: form.donationSettings.currency.trim() || "LAK",
      minAmount,
      maxAmount,
      suggestedAmounts: parseSuggestedAmounts(
        form.donationSettings.suggestedAmounts,
      ),
      allowedPaymentMethods,
      allowOfflineDonations: form.donationSettings.allowOfflineDonations,
    },
  };
};

export default function CustomizeDonationPage() {
  const queryClient = useQueryClient();
  const [token] = useLocalStorage("token", "");
  const [storedBusinessId] = useLocalStorage("businessId", "");
  const businessId = storedBusinessId || DONATE_BUSINESS_ID;
  const profileQueryKey = ["donation-customize-auth-me", businessId || "self"];
  const hasUserEditedRef = useRef(false);
  const [uploadState, setUploadState] = useState<{
    field?: UploadField;
    progress: number;
  }>({ progress: 0 });
  const [form, setForm] = useState<StreamerProfileForm>(() =>
    normalizeForm({}),
  );

  const { data: userResponse, isLoading } = useQuery(
    profileQueryKey,
    () => userGetMe({ token, businessId }),
    {
      enabled: Boolean(token),
      refetchOnMount: "always",
      retry: false,
    },
  );

  const user = useMemo(
    () => getStreamerProfileData(userResponse),
    [userResponse],
  );

  useEffect(() => {
    if (!userResponse || hasUserEditedRef.current) return;
    setForm(normalizeForm(user));
  }, [user, userResponse]);

  useEffect(() => {
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    window.localStorage.removeItem(STORAGE_KEY);
  }, []);

  const updateForm = (
    updater:
      | Partial<StreamerProfileForm>
      | ((current: StreamerProfileForm) => StreamerProfileForm),
  ) => {
    hasUserEditedRef.current = true;
    setForm((current) =>
      typeof updater === "function"
        ? updater(current)
        : { ...current, ...updater },
    );
  };

  const updateSocialLink = (key: SocialKey, value: string) => {
    updateForm((current) => ({
      ...current,
      socialLinks: {
        ...current.socialLinks,
        [key]: value,
      },
    }));
  };

  const updateDonationSetting = (
    key: "currency" | "minAmount" | "maxAmount" | "suggestedAmounts",
    value: string,
  ) => {
    updateForm((current) => ({
      ...current,
      donationSettings: {
        ...current.donationSettings,
        [key]: value,
      },
    }));
  };

  const togglePaymentMethod = (value: string) => {
    updateForm((current) => {
      const allowedPaymentMethods =
        current.donationSettings.allowedPaymentMethods.includes(value)
          ? current.donationSettings.allowedPaymentMethods.filter(
              (item) => item !== value,
            )
          : [...current.donationSettings.allowedPaymentMethods, value];

      return {
        ...current,
        donationSettings: {
          ...current.donationSettings,
          allowedPaymentMethods,
        },
      };
    });
  };

  const handleImageUpload = async (field: UploadField, file: File) => {
    if (!token) {
      errorAdd("ກະລຸນາ login ກ່ອນອັບໂຫຼດຮູບ");
      return;
    }
    if (!isAllowedImage(file)) {
      errorAdd("ຮອງຮັບຮູບ JPG, PNG, WEBP, HEIC ຫຼື HEIF ເທົ່ານັ້ນ");
      return;
    }

    setUploadState({ field, progress: 1 });

    try {
      const uploaded = await uploadDonateProfileImage({
        file,
        token,
        businessId,
        onProgress: (progress) => setUploadState({ field, progress }),
      });

      updateForm({ [field]: uploaded.url } as Partial<StreamerProfileForm>);
      successAdd("ອັບໂຫຼດຮູບສຳເລັດ");
    } catch (error: any) {
      errorAdd(donateErrorMessage(error));
    } finally {
      setUploadState({ progress: 0 });
    }
  };

  const saveMutation = useMutation(
    async () => {
      if (!token) throw new Error("Token is required");

      return donateStreamerUpdateProfile({
        body: buildPayload(form),
        token,
        businessId,
      });
    },
    {
      onSuccess: () => {
        if (typeof window !== "undefined") {
          window.localStorage.removeItem(STORAGE_KEY);
        }
        hasUserEditedRef.current = false;
        successAdd("ບັນທຶກໂປຣໄຟລ໌ສຳເລັດ");
        queryClient.invalidateQueries(profileQueryKey);
        queryClient.invalidateQueries(["user-me"]);
      },
      onError: (error: any) => {
        errorAdd(donateErrorMessage(error));
      },
    },
  );

  const isSavingDisabled =
    saveMutation.isLoading || isLoading || Boolean(uploadState.field) || !token;

  return (
    <main className="min-h-full min-w-0">
      <div className="flex w-full flex-col gap-4">
        <section className="rounded-lg border border-[#dbe4ef] bg-white p-4 shadow-[0_8px_24px_rgba(16,24,40,0.04)]">
          <SectionHeader
            icon={<FiUser className="h-4 w-4" />}
            iconClassName="bg-[#eef7ff] text-[#168feb]"
            title="ໂປຣໄຟລ໌ສະຕຣີມເມີ"
          />

          <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[200px_1fr]">
            <ImageUploadField
              label="PROFILE IMAGE"
              value={form.image}
              variant="avatar"
              disabled={Boolean(uploadState.field)}
              isUploading={uploadState.field === "image"}
              progress={uploadState.progress}
              onUpload={(file) => handleImageUpload("image", file)}
              onRemove={() => updateForm({ image: "" })}
            />
            <ImageUploadField
              label="COVER IMAGE"
              value={form.coverImage}
              variant="cover"
              disabled={Boolean(uploadState.field)}
              isUploading={uploadState.field === "coverImage"}
              progress={uploadState.progress}
              onUpload={(file) => handleImageUpload("coverImage", file)}
              onRemove={() => updateForm({ coverImage: "" })}
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-2">
            <FormField label="ຊື່ສະຕຣີມເມີ">
              <input
                value={form.name}
                onChange={(event) => updateForm({ name: event.target.value })}
                className="h-9 w-full rounded-lg border border-[#dbe4ef] bg-[#f6f8fb] px-3 text-sm font-medium text-[#344054] outline-none transition focus:border-[#168feb] focus:bg-white focus:ring-2 focus:ring-[#d9efff]"
                placeholder="Streamer Name"
              />
            </FormField>
            <FormField label="USERNAME">
              <input
                value={form.username}
                className="h-9 w-full cursor-not-allowed rounded-lg border border-[#dbe4ef] bg-[#eef2f6] px-3 text-sm font-medium text-[#667085] outline-none"
                placeholder="streamer123"
                autoComplete="username"
                disabled
                readOnly
              />
            </FormField>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-[1fr_220px]">
            <FormField label="BIO">
              <textarea
                value={form.bio}
                onChange={(event) => updateForm({ bio: event.target.value })}
                className="min-h-[92px] w-full resize-y rounded-lg border border-[#dbe4ef] bg-[#f6f8fb] px-3 py-2 text-sm font-medium text-[#344054] outline-none transition focus:border-[#168feb] focus:bg-white focus:ring-2 focus:ring-[#d9efff]"
                placeholder="ແນະນຳຕົວສັ້ນໆ"
              />
            </FormField>
          </div>
        </section>

        <section className="rounded-lg border border-[#dbe4ef] bg-white p-4 shadow-[0_8px_24px_rgba(16,24,40,0.04)]">
          <SectionHeader
            icon={<FiLink className="h-4 w-4" />}
            iconClassName="bg-[#f0fdf4] text-[#16a34a]"
            title="ລິ້ງຊ່ອງທາງສະຕຣີມ"
          />

          <div className="mt-4 grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-2">
            {socialFields.map((field) => {
              const Icon = field.icon;
              return (
                <FormField
                  key={field.key}
                  label={field.label}
                  icon={<Icon className={`h-3.5 w-3.5 ${field.iconClass}`} />}
                >
                  <input
                    value={form.socialLinks[field.key]}
                    onChange={(event) =>
                      updateSocialLink(field.key, event.target.value)
                    }
                    className="h-9 w-full rounded-lg border border-[#dbe4ef] bg-[#f6f8fb] px-3 text-sm font-medium text-[#344054] outline-none transition focus:border-[#168feb] focus:bg-white focus:ring-2 focus:ring-[#d9efff]"
                    placeholder={field.placeholder}
                  />
                </FormField>
              );
            })}
          </div>
        </section>
        {/*
        <section className="rounded-lg border border-[#dbe4ef] bg-white p-4 shadow-[0_8px_24px_rgba(16,24,40,0.04)]">
          <SectionHeader
            icon={<FiDollarSign className="h-4 w-4" />}
            iconClassName="bg-[#fff7ed] text-[#f59e0b]"
            title="ຕັ້ງຄ່າການ Donate"
          />

          <div className="mt-4 grid grid-cols-1 gap-x-4 gap-y-3 lg:grid-cols-4">
            <FormField label="CURRENCY">
              <input
                value={form.donationSettings.currency}
                onChange={(event) =>
                  updateDonationSetting("currency", event.target.value)
                }
                className="h-9 w-full rounded-lg border border-[#dbe4ef] bg-[#f6f8fb] px-3 text-sm font-medium uppercase text-[#344054] outline-none transition focus:border-[#168feb] focus:bg-white focus:ring-2 focus:ring-[#d9efff]"
                placeholder="LAK"
              />
            </FormField>
            <FormField label="MIN AMOUNT">
              <input
                type="number"
                min={0}
                step={1000}
                value={form.donationSettings.minAmount}
                onChange={(event) =>
                  updateDonationSetting("minAmount", event.target.value)
                }
                className="h-9 w-full rounded-lg border border-[#dbe4ef] bg-[#f6f8fb] px-3 text-sm font-medium text-[#344054] outline-none transition focus:border-[#168feb] focus:bg-white focus:ring-2 focus:ring-[#d9efff]"
              />
            </FormField>
            <FormField label="MAX AMOUNT">
              <input
                type="number"
                min={0}
                step={1000}
                value={form.donationSettings.maxAmount}
                onChange={(event) =>
                  updateDonationSetting("maxAmount", event.target.value)
                }
                className="h-9 w-full rounded-lg border border-[#dbe4ef] bg-[#f6f8fb] px-3 text-sm font-medium text-[#344054] outline-none transition focus:border-[#168feb] focus:bg-white focus:ring-2 focus:ring-[#d9efff]"
              />
            </FormField>
            <FormField label="SUGGESTED AMOUNTS">
              <input
                value={form.donationSettings.suggestedAmounts}
                onChange={(event) =>
                  updateDonationSetting("suggestedAmounts", event.target.value)
                }
                className="h-9 w-full rounded-lg border border-[#dbe4ef] bg-[#f6f8fb] px-3 text-sm font-medium text-[#344054] outline-none transition focus:border-[#168feb] focus:bg-white focus:ring-2 focus:ring-[#d9efff]"
                placeholder="10000, 20000, 50000"
              />
            </FormField>
          </div>

          <div className="mt-4">
            <div className="mb-2 text-[11px] font-bold uppercase text-[#475467]">
              PAYMENT METHODS
            </div>
            <div className="flex flex-wrap gap-2">
              {paymentMethodOptions.map((option) => {
                const selected =
                  form.donationSettings.allowedPaymentMethods.includes(
                    option.value,
                  );
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`flex h-10 min-w-[132px] items-center gap-2 rounded-lg border px-3 text-sm font-medium transition ${
                      selected
                        ? "border-[#168feb] bg-[#eef7ff] text-[#0e67ad]"
                        : "border-[#dbe4ef] bg-white text-[#475467] hover:border-[#b9dcff] hover:bg-[#f8fbff]"
                    }`}
                    onClick={() => togglePaymentMethod(option.value)}
                    aria-pressed={selected}
                  >
                    <span
                      className={`grid h-4 w-4 flex-none place-items-center rounded border ${
                        selected
                          ? "border-[#168feb] bg-[#168feb]"
                          : "border-[#b8c5d6]"
                      }`}
                    >
                      {selected && <span className="h-2 w-2 bg-white" />}
                    </span>
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4">
            <button
              type="button"
              className={`flex h-10 min-w-[220px] items-center justify-between gap-3 rounded-lg border px-3 text-sm font-bold transition ${
                form.donationSettings.allowOfflineDonations
                  ? "border-[#12b76a] bg-[#ecfdf3] text-[#027a48]"
                  : "border-[#dbe4ef] bg-[#f6f8fb] text-[#667085]"
              }`}
              onClick={() =>
                updateForm((current) => ({
                  ...current,
                  donationSettings: {
                    ...current.donationSettings,
                    allowOfflineDonations:
                      !current.donationSettings.allowOfflineDonations,
                  },
                }))
              }
              aria-pressed={form.donationSettings.allowOfflineDonations}
            >
              <span>Allow offline donations</span>
              <span
                className={`h-4 w-8 rounded-full p-0.5 transition ${
                  form.donationSettings.allowOfflineDonations
                    ? "bg-[#12b76a]"
                    : "bg-[#98a2b3]"
                }`}
              >
                <span
                  className={`block h-3 w-3 rounded-full bg-white transition ${
                    form.donationSettings.allowOfflineDonations
                      ? "translate-x-4"
                      : ""
                  }`}
                />
              </span>
            </button>
          </div>


        </section> */}
        <div className="flex justify-end mb-5">
          <Button
            variant="primary"
            className="inline-flex min-h-[40px] min-w-[112px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-bold"
            disabled={isSavingDisabled}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FiSave />
            )}
            ບັນທຶກ
          </Button>
        </div>
      </div>
    </main>
  );
}

function SectionHeader({
  icon,
  iconClassName,
  title,
}: {
  icon: React.ReactNode;
  iconClassName: string;
  title: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div
        className={`flex h-9 w-9 flex-none items-center justify-center rounded-full ${iconClassName}`}
      >
        {icon}
      </div>
      <div className="min-w-0 pt-0.5">
        <h1 className="mb-1 text-sm font-black text-[#101828]">{title}</h1>
      </div>
    </div>
  );
}

function FormField({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="block">
      <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase text-[#475467]">
        {icon}
        {label}
      </span>
      {children}
    </div>
  );
}

function ImageUploadField({
  label,
  value,
  variant,
  disabled,
  isUploading,
  progress,
  onUpload,
  onRemove,
}: {
  label: string;
  value: string;
  variant: "avatar" | "cover";
  disabled: boolean;
  isUploading: boolean;
  progress: number;
  onUpload: (file: File) => void;
  onRemove: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputIdRef = useRef(
    `streamer-profile-image-${Math.random().toString(36).slice(2)}`,
  );
  const imageSrc = getImageSrc(value);
  const isCover = variant === "cover";

  return (
    <div className={isCover ? "min-w-0" : "w-[180px] max-w-full"}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-[11px] font-bold uppercase text-[#475467]">
          {label}
        </span>
        {value && !isUploading ? (
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-[#dbe4ef] bg-white text-[#667085] transition hover:border-[#fda29b] hover:text-[#d92d20]"
            onClick={onRemove}
            aria-label="Remove image"
          >
            <FiX className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        id={inputIdRef.current}
        type="file"
        accept={acceptedImageTypes}
        className="sr-only"
        disabled={disabled}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onUpload(file);
          event.currentTarget.value = "";
        }}
      />

      <button
        type="button"
        className={`relative flex overflow-hidden rounded-lg border border-dashed border-[#b8c5d6] bg-[#f6f8fb] text-[#667085] transition hover:border-[#168feb] hover:bg-[#f8fbff] ${
          isCover ? "h-[178px] w-full" : "h-[160px] w-[160px]"
        }`}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        {imageSrc ? (
          <img src={imageSrc} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="grid h-full w-full place-items-center">
            <span className="flex flex-col items-center gap-2 text-xs font-bold">
              {isCover ? (
                <FiImage className="h-7 w-7" />
              ) : (
                <FiUploadCloud className="h-7 w-7" />
              )}
              ອັບໂຫຼດ
            </span>
          </span>
        )}

        {imageSrc && !isUploading ? (
          <span className="absolute inset-x-0 bottom-0 flex h-9 items-center justify-center bg-black/45 text-xs font-bold text-white">
            ປ່ຽນຮູບ
          </span>
        ) : null}

        {isUploading ? (
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/80 text-xs font-bold text-[#344054]">
            <Spinner animation="border" size="sm" variant="primary" />
            {progress}%
          </span>
        ) : null}
      </button>
    </div>
  );
}
